import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '@/api/client';
import posthog from 'posthog-js';

const WHATSAPP_LINK = 'https://chat.whatsapp.com/DxSiWFhNYffEKpwZRGPa9k';

interface WaitlistFormProps {
  /** When provided (e.g. inside a modal), used instead of navigating to "/" on success/close. */
  onClose?: () => void;
}

const WaitlistForm = ({ onClose }: WaitlistFormProps) => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    role: '',
    role_other: '',
    location: '',
    work_type: '',
    work_type_other: '',
    linkedin: '',
    monthly_earnings: '',
    pain_points: '',
    contract_disputes: '',
    active_contracts: '',
    international: '',
    source: '',
    source_other: '',
    agreed: false,
  });

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const val = e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
    setFormData(prev => ({ ...prev, [e.target.name]: val }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    // Resolve "Other" free-text fields before submitting
    const payload = {
      ...formData,
      role: formData.role === 'Other' ? (formData.role_other.trim() || 'Other') : formData.role,
      work_type: formData.work_type === 'Other' ? (formData.work_type_other.trim() || 'Other') : formData.work_type,
      source: formData.source === 'Other' ? (formData.source_other.trim() || 'Other') : formData.source,
      // interview field removed — send a default so the NOT NULL column is satisfied
      interview: 'N/A',
    };

    try {
      const res = await fetch(`${API_BASE}/api/v1/public/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data?.error || 'Something went wrong. Please try again.');
        return;
      }
      
      // Track Lead event in Meta Pixel on successful submission
      if (typeof window !== 'undefined' && (window as any).fbq) {
        (window as any).fbq('track', 'Lead');
      }

      posthog.capture("waitlist_joined");
      setSuccess(true);
    } catch {
      setError('Network error. Please check your connection.');
    } finally {
      setSubmitting(false);
    }
  };

  const goHome = () => {
    if (onClose) onClose();
    else navigate('/');
  };

  const selectClass = (val: string) =>
    `w-full bg-transparent border border-gray-700 rounded-md pl-4 pr-10 py-3 text-sm focus:outline-none focus:border-gray-400 transition-colors appearance-none cursor-pointer ${!val ? 'text-gray-500' : 'text-white'}`;

  const inputClass = 'w-full bg-transparent border border-gray-700 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-gray-400 transition-colors placeholder-gray-500';

  if (success) {
    return (
      <div className="flex flex-col items-center justify-center px-4 py-16 text-white">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-accent/10 border border-accent/30 flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-3xl font-semibold mb-3">You're on the list!</h2>
          <p className="text-gray-400 text-lg mb-4">We'll reach out when we're ready to onboard you. Keep an eye on your inbox.</p>
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full bg-accent/10 border border-accent/30 text-accent text-sm font-bold">
            Your 1 month of Unlimited (free) is locked in
          </div>
          <p className="text-gray-500 text-sm mb-8">In the meantime, join our WhatsApp community to stay updated and connect with other early members.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#25D366] hover:bg-[#1ebe5d] rounded-full text-black font-semibold transition-colors"
            >
              <WhatsAppIcon />
              Join our WhatsApp community
            </a>
            <button
              onClick={goHome}
              className="px-6 py-3 bg-[#2a2a2a] hover:bg-[#333] border border-gray-600 rounded-full text-gray-200 transition-colors"
            >
              {onClose ? 'Close' : 'Back to home'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto px-4 py-12 sm:py-16">
      <div className="text-center mb-12">
        <h1 className="text-4xl sm:text-5xl font-semibold mb-4 tracking-tight text-white">Join the beta</h1>
        <p className="text-gray-300 text-lg">Defellix turns freelance agreements into verifiable, blockchain-anchored contracts with portable reputation. We're onboarding early users now.</p>
        <div className="inline-flex items-center gap-2 mt-5 px-4 py-2 rounded-full bg-accent/10 border border-accent/30 text-accent text-sm font-bold">
          Get 1 month of the Unlimited plan free when you're onboarded
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Full Name */}
          <input type="text" name="full_name" placeholder="Full Name *" required value={formData.full_name} onChange={handleChange} className={inputClass} />

          {/* Email */}
          <input type="email" name="email" placeholder="Email *" required value={formData.email} onChange={handleChange} className={inputClass} />

          {/* Role */}
          <div className="flex flex-col gap-2">
            <div className="relative">
              <select name="role" required value={formData.role} onChange={handleChange} className={selectClass(formData.role)}>
                <option value="" disabled className="bg-[#0f1117] text-gray-200">I am primarily a... *</option>
                <option value="Freelancer" className="bg-[#0f1117] text-gray-200">Freelancer</option>
                <option value="Client" className="bg-[#0f1117] text-gray-200">Client (I hire freelancers)</option>
                <option value="Agency" className="bg-[#0f1117] text-gray-200">Agency</option>
                <option value="Both" className="bg-[#0f1117] text-gray-200">Both — I freelance and hire</option>
                <option value="Other" className="bg-[#0f1117] text-gray-200">Other</option>
              </select>
              <ChevronDown />
            </div>
            {formData.role === 'Other' && (
              <input type="text" name="role_other" placeholder="Please specify *" required value={formData.role_other} onChange={handleChange} className={inputClass} />
            )}
          </div>

          {/* Location */}
          <input type="text" name="location" placeholder="Where are you based? (city, country) *" required value={formData.location} onChange={handleChange} className={inputClass} />

          {/* Work Type */}
          <div className="flex flex-col gap-2">
            <div className="relative">
              <select name="work_type" required value={formData.work_type} onChange={handleChange} className={selectClass(formData.work_type)}>
                <option value="" disabled className="bg-[#0f1117] text-gray-200">Primary work type *</option>
                {['Developer / Engineer', 'Designer (UI/UX/Graphic)', 'Content Writer / Copywriter', 'Video Editor / Motion', 'Marketing / SEO', 'Consultant / Advisor', 'Product Manager', 'Illustrator / Artist', 'Photographer / Videographer', 'Other'].map(o => (
                  <option key={o} value={o} className="bg-[#0f1117] text-gray-200">{o}</option>
                ))}
              </select>
              <ChevronDown />
            </div>
            {formData.work_type === 'Other' && (
              <input type="text" name="work_type_other" placeholder="Please specify your work type *" required value={formData.work_type_other} onChange={handleChange} className={inputClass} />
            )}
          </div>

          {/* Social Media Links */}
          <input type="text" name="linkedin" placeholder="Social Media Links (example: Instagram, LinkedIn, etc.)" value={formData.linkedin} onChange={handleChange} className={inputClass} />
        </div>

        {/* Pain Points */}
        <textarea name="pain_points" placeholder="What's your biggest frustration with freelance contracts or payments today? *" required value={formData.pain_points} onChange={handleChange} rows={3}
          className={`${inputClass} resize-none`} />

        {/* Contract Disputes */}
        <textarea name="contract_disputes" placeholder="Have you ever lost money due to a late payment or contract dispute? Tell us briefly (optional)" value={formData.contract_disputes} onChange={handleChange} rows={2}
          className={`${inputClass} resize-none`} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Monthly Earnings */}
          <div className="relative">
            <select name="monthly_earnings" value={formData.monthly_earnings} onChange={handleChange} className={selectClass(formData.monthly_earnings)}>
              <option value="" disabled className="bg-[#0f1117] text-gray-200">Avg. monthly freelance earnings (optional)</option>
              <option value="Under ₹10,000/month" className="bg-[#0f1117] text-gray-200">Under ₹10,000 / month</option>
              <option value="₹10,000–₹30,000/month" className="bg-[#0f1117] text-gray-200">₹10,000 – ₹30,000 / month</option>
              <option value="₹30,000–₹75,000/month" className="bg-[#0f1117] text-gray-200">₹30,000 – ₹75,000 / month</option>
              <option value="₹75,000–₹2,00,000/month" className="bg-[#0f1117] text-gray-200">₹75,000 – ₹2,00,000 / month</option>
              <option value="Above ₹2,00,000/month" className="bg-[#0f1117] text-gray-200">Above ₹2,00,000 / month</option>
            </select>
            <ChevronDown />
          </div>

          {/* Active Contracts */}
          <div className="relative">
            <select name="active_contracts" value={formData.active_contracts} onChange={handleChange} className={selectClass(formData.active_contracts)}>
              <option value="" disabled className="bg-[#0f1117] text-gray-200">Active contracts at once (optional)</option>
              <option value="1–2" className="bg-[#0f1117] text-gray-200">1–2</option>
              <option value="3–5" className="bg-[#0f1117] text-gray-200">3–5</option>
              <option value="6–10" className="bg-[#0f1117] text-gray-200">6–10</option>
              <option value="10+" className="bg-[#0f1117] text-gray-200">10+</option>
            </select>
            <ChevronDown />
          </div>

          {/* International */}
          <div className="relative md:col-span-2">
            <select name="international" value={formData.international} onChange={handleChange} className={selectClass(formData.international)}>
              <option value="" disabled className="bg-[#0f1117] text-gray-200">Do you work with international clients? (optional)</option>
              <option value="Yes — mostly international" className="bg-[#0f1117] text-gray-200">Yes — mostly international</option>
              <option value="Mix of both" className="bg-[#0f1117] text-gray-200">Mix of both</option>
              <option value="No — mostly local" className="bg-[#0f1117] text-gray-200">No — mostly local</option>
            </select>
            <ChevronDown />
          </div>
        </div>

        {/* Source */}
        <div className="flex flex-col gap-2">
          <div className="relative">
            <select name="source" value={formData.source} onChange={handleChange} className={selectClass(formData.source)}>
              <option value="" disabled className="bg-[#0f1117] text-gray-200">How did you hear about Defellix? (optional)</option>
              {['Friend / Word of mouth', 'LinkedIn', 'Instagram', 'Reddit', 'X / Twitter', 'Google Search', 'AI Tool', 'Other'].map(o => (
                <option key={o} value={o} className="bg-[#0f1117] text-gray-200">{o}</option>
              ))}
            </select>
            <ChevronDown />
          </div>
          {formData.source === 'Other' && (
            <input type="text" name="source_other" placeholder="Please tell us where you heard about us" value={formData.source_other} onChange={handleChange} className={inputClass} />
          )}
        </div>

        {/* Agreement */}
        <div className="pt-4 pb-2">
          <label className="flex items-start gap-3 cursor-pointer group">
            <div className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded border flex items-center justify-center transition-colors ${formData.agreed ? 'bg-accent border-accent' : 'border-gray-600 group-hover:border-gray-400'}`}>
              {formData.agreed && <svg className="w-3.5 h-3.5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
            </div>
            <input type="checkbox" name="agreed" className="hidden" checked={formData.agreed} onChange={handleChange} required />
            <span className="text-sm text-gray-400 leading-relaxed">
              I agree to be contacted about the Defellix beta. We'll only use this to reach out about early access — no spam, ever. *
            </span>
          </label>
        </div>

        {error && (
          <p className="text-red-400 text-sm bg-red-500/10 rounded-lg px-4 py-3">{error}</p>
        )}

        <div className="pt-4">
          <button type="submit" disabled={submitting}
            className="px-6 py-3 bg-[#2a2a2a] hover:bg-[#333] border border-gray-600 rounded-full text-gray-200 text-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {submitting ? 'Submitting…' : 'Submit Application'}
          </button>
        </div>
      </form>
    </div>
  );
};

function ChevronDown() {
  return (
    <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
      <svg className="w-4 h-4 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
      </svg>
    </div>
  );
}

function WhatsAppIcon() {
  return (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

export default WaitlistForm;
